import { useAuth0 } from "@auth0/auth0-react";
import { useFormikContext } from "formik";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Loader } from "../common/progress/Loader";
import { ExtendedUser } from "../identity/models/ExtendedUser";
import { usePluginActions } from "./hooks/usePluginActions";
import { pluginValidationSchema } from "./schemas/PluginValidationSchema";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useState } from "react";
import { RejectionReasonModal } from "./RejectionReasonModal";
import { PluginDetails } from "./models/PluginDetails";
import { useParams } from "react-router-dom";
import { ReviewType } from "./models/ReviewType";
import { processMarkdownToHTML } from "../../utils";

type PendingPluginButtonGroupProps = { canBeApproved: boolean };

export const PendingPluginButtonGroup = ({ canBeApproved }: PendingPluginButtonGroupProps) => {
  const { buttonStyle } = useTheme();
  const { user } = useAuth0<ExtendedUser>();
  const { isLoading, pluginActions } = usePluginActions();
  const { values, setTouched, handleSubmit: submit } = useFormikContext<PluginDetails>();
  const [showModal, setShowModal] = useState(false);
  const { translate } = useLocalization();
  const { pluginId } = useParams() as { pluginId: string };
  const isSystemAdmin = user?.user_metadata?.isSystemAdmin;

  const handleSave = async () => {
    try {
      values.htmlDescription = await processMarkdownToHTML(values.markdownDescription, values.descriptionResources);
      values.htmlDocumentation = await processMarkdownToHTML(
        values.markdownDocumentation,
        values.documentationResources
      );
      await pluginValidationSchema(translate).validate(values, { abortEarly: false });
      setTouched({});
      await pluginActions.handleUpdatePending(parseInt(pluginId), values);
    } catch (errors) {
      submit();
    }
  };

  if (isLoading) return <Loader />;

  if (isSystemAdmin) {
    return (
      <>
        {canBeApproved ? (
          <Button
            style={buttonStyle}
            onClick={() => pluginActions.handleRequestReview(parseInt(pluginId), ReviewType.Approved)}
          >
            {translate("plugins.approve")}
          </Button>
        ) : (
          <OverlayTrigger overlay={<Tooltip>{translate("versions.reviewPluginVersion")}</Tooltip>}>
            <span className="d-inline-block">
              <Button style={buttonStyle} disabled>
                {translate("plugins.approve")}
              </Button>
            </span>
          </OverlayTrigger>
        )}
        <Button variant="danger" className="mx-2" onClick={() => setShowModal(true)}>
          {translate("plugins.reject")}
        </Button>
        {showModal && (
          <RejectionReasonModal
            onContinue={(rejectionReason) =>
              pluginActions.handleRequestReview(parseInt(pluginId), ReviewType.Rejected, rejectionReason)
            }
            onClose={() => setShowModal(false)}
          />
        )}
      </>
    );
  }

  return (
    <Button style={buttonStyle} className="me-2" type="button" onClick={() => handleSave()}>
      {values.publishToRWSAppStore ? translate("plugins.submit") : translate("common.save")}
    </Button>
  );
};
